import {
  collection,
  doc,
  getDocs,
  query,
  runTransaction,
  where,
} from 'firebase/firestore';
import firebaseFirestore from '~/config/firebase/firestore';
import { Location } from '~/core/types';
import { format } from 'date-fns';
import esLocale from 'date-fns/locale/es';
import { formatDate, getDatetimeFromString, getTimestampZonedTimeToUtc } from '~/utils/date';
import { plazasPorFranja } from './serie';

/**
 * Escritura de una reserva que se repite.
 *
 * Las N copias se crean en UNA sola transacción junto con la ocupación de las
 * salas. O entran todas, o no entra ninguna: una serie a medias obligaría a
 * adivinar qué se creó y qué no, y dejaría al cliente con parte de sus días
 * reservados sin que nadie se entere.
 *
 * La transacción vuelve a leer el estado real de cada sala antes de escribir,
 * así que si alguien reservó entre la vista previa y el "Crear", esa fecha se
 * detecta aquí y no se pisa nada.
 */

/** Una fecha de la serie, ya decidida: dónde va y qué franjas ocupa. */
export type FechaPlanificada = {
  /** YYYY-MM-DD */
  fecha: string;
  locationId: string;
  franjas: string[];
};

export type ResultadoSerie = {
  creadas: { code: string; fecha: string; locationName: string }[];
};

/** Se lanza cuando una fecha dejó de caber mientras se revisaba. */
export class FechaOcupadaError extends Error {
  constructor(
    public readonly fecha: string,
    public readonly hora: string,
    public readonly locationName?: string
  ) {
    super(
      `El ${fecha} a las ${hora} ya no hay sitio en ${locationName ?? 'la sala'}. ` +
        'Vuelve a revisar las fechas.'
    );
    this.name = 'FechaOcupadaError';
  }
}

/** Admite el formato antiguo '08:00 AM' además de 'HH:mm'. */
const normalizarHora = (valor?: string | null): string | null => {
  if (!valor) return null;
  const m = /^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i.exec(valor.trim());
  if (!m) return null;
  let h = Number(m[1]);
  const periodo = m[3]?.toUpperCase();
  if (periodo === 'PM' && h < 12) h += 12;
  if (periodo === 'AM' && h === 12) h = 0;
  return `${String(h).padStart(2, '0')}:${m[2]}`;
};

const CARACTERES = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
const codigoAlAzar = () =>
  Array.from({ length: 4 }, () => CARACTERES[Math.floor(Math.random() * CARACTERES.length)]).join(
    ''
  );

/**
 * Códigos únicos para toda la serie, reservados ANTES de la transacción.
 *
 * Dentro de una transacción no se pueden hacer consultas, y el código tiene que
 * ser único en toda la colección. Se comprueban aquí y se llevan hechos.
 */
const reservarCodigos = async (cuantos: number): Promise<string[]> => {
  const codigos = new Set<string>();
  let intentos = 0;

  while (codigos.size < cuantos && intentos < cuantos * 20) {
    intentos++;
    const candidato = codigoAlAzar().toUpperCase();
    if (codigos.has(candidato)) continue;

    const encontrado = await getDocs(
      query(collection(firebaseFirestore, 'reservations'), where('code', '==', candidato))
    );
    if (encontrado.empty) codigos.add(candidato);
  }

  if (codigos.size < cuantos) {
    throw new Error('No se pudieron generar los códigos de las reservas. Inténtalo de nuevo.');
  }
  return [...codigos];
};

/** "05:00" → "5 am"; "10:30" → "10:30 am". Como lo diría una persona. */
export const hora12 = (hhmm: string): string => {
  const [h, m] = hhmm.split(':').map(Number);
  const periodo = h < 12 ? 'am' : 'pm';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return m === 0 ? `${h12} ${periodo}` : `${h12}:${String(m).padStart(2, '0')} ${periodo}`;
};

/**
 * Una línea del correo: "viernes 11 de septiembre, de 5 am a 10 am, Sala E2S1".
 *
 * Lleva la sala en cada línea porque una fecha suelta puede haber acabado en
 * otra sala, si la suya estaba ocupada ese día.
 */
export const lineaDeFecha = ({
  fecha,
  horaInicio,
  horaFin,
  sala,
  diaCompleto,
  ingles,
}: {
  fecha: Date;
  horaInicio: string;
  horaFin: string | null;
  sala: string;
  diaCompleto: boolean;
  ingles: boolean;
}): string => {
  const dia = ingles
    ? format(fecha, 'EEEE, MMMM d')
    : format(fecha, "EEEE d 'de' MMMM", { locale: esLocale });

  const cuando = diaCompleto
    ? ingles
      ? 'all day'
      : 'todo el día'
    : horaFin
    ? ingles
      ? `from ${hora12(horaInicio)} to ${hora12(horaFin)}`
      : `de ${hora12(horaInicio)} a ${hora12(horaFin)}`
    : ingles
    ? `at ${hora12(horaInicio)}`
    : `a las ${hora12(horaInicio)}`;

  return `${dia}, ${cuando}, ${sala}`;
};

/**
 * El texto sustituye a `{{recurrenceFechas}}` dentro del HTML del correo, así
 * que las líneas se separan con `<br />`. Se escapa lo que pueda romper el
 * HTML, y los `$` porque `String.replace` les da significado propio.
 */
const comoListaHtml = (lineas: string[]): string =>
  lineas
    .map((l) =>
      l
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\$/g, '$$$$')
    )
    .join('<br />');

/** La sala tal como se guarda dentro de la reserva: sin su agenda ni su horario. */
const salaParaGuardar = (location: Location) => {
  const copia: Partial<Location> = { ...location };
  delete copia.reservations;
  delete copia.schedule;
  return copia;
};

/**
 * Crea las copias de una reserva en las fechas indicadas.
 *
 * @param base       La reserva original, tal cual está en Firestore.
 * @param fechas     Fechas ya decididas (con su sala, que puede no ser la original).
 * @param salas      Las salas implicadas, para leer sus ajustes.
 * @param recurrenceId  Identificador común de la serie.
 * @param avisarPorCorreo  Manda UN solo correo con todas las fechas. El truco:
 *   el servidor solo avisa cuando una reserva pasa de no tener código a
 *   tenerlo, así que las copias nacen con el suyo puesto —y calladas— menos la
 *   primera, que lo recibe en un segundo paso y dispara ese único correo.
 * @throws {FechaOcupadaError} si alguna fecha dejó de caber.
 */
export const crearSerie = async ({
  base,
  baseId,
  fechas,
  salas,
  recurrenceId,
  descripcion,
  creadaPor,
  avisarPorCorreo = false,
  codigoBasePendiente,
}: {
  base: Record<string, any>;
  baseId: string;
  fechas: FechaPlanificada[];
  salas: Record<string, Location>;
  recurrenceId: string;
  descripcion: string;
  creadaPor: unknown;
  avisarPorCorreo?: boolean;
  /**
   * Código que la reserva del día tiene reservado pero aún no escrito. Cuando
   * viene, es ella la que entra en la serie y la que manda el único correo.
   */
  codigoBasePendiente?: string;
}): Promise<ResultadoSerie> => {
  if (!fechas.length) return { creadas: [] };

  const codigos = await reservarCodigos(fechas.length);
  const personas = Math.max(Number(base.numberPeople ?? 1), 1);
  const horaInicio = normalizarHora(base.startHour) ?? normalizarHora(base.time);
  const horaFin = normalizarHora(base.endHour);

  if (!horaInicio) {
    throw new Error('La reserva original no tiene una hora de inicio legible.');
  }

  // Referencias de los documentos nuevos: se necesitan sus ids ya, porque la
  // ocupación de la sala se guarda con el id de la reserva.
  // La reserva del día cuenta como la primera de la serie cuando es ella la
  // que va a avisar: al cliente hay que decirle cuántas reservas tiene, no
  // cuántas copias se hicieron.
  const conBase = Boolean(codigoBasePendiente);
  const total = fechas.length + (conBase ? 1 : 0);

  const nuevas = fechas.map((f, i) => ({
    ...f,
    ref: doc(collection(firebaseFirestore, 'reservations')),
    code: codigos[i],
    indice: i + 1 + (conBase ? 1 : 0),
  }));

  const salasImplicadas = [...new Set(fechas.map((f) => f.locationId))];

  /**
   * Las fechas tal como las verá el cliente en el correo, una por línea y con
   * su sala. Se arma aquí, en el idioma de la reserva y en inglés, porque la
   * plantilla del servidor solo sabe sustituir campos: no puede recorrer nada.
   */
  const armarLineas = (ingles: boolean): string => {
    const lineas: string[] = [];

    if (conBase) {
      const salaBase = salas[base.location?.id] ?? undefined;
      lineas.push(
        lineaDeFecha({
          fecha: getDatetimeFromString(base.date, horaInicio)!,
          horaInicio,
          horaFin,
          sala: base.location?.name ?? '',
          diaCompleto: Boolean(salaBase?.schedule?.settings?.isReservationWholeDay),
          ingles,
        })
      );
    }

    nuevas.forEach((n) => {
      const sala = salas[n.locationId];
      lineas.push(
        lineaDeFecha({
          fecha: getDatetimeFromString(n.fecha, horaInicio)!,
          horaInicio,
          horaFin,
          sala: sala?.name ?? '',
          diaCompleto: Boolean(sala?.schedule?.settings?.isReservationWholeDay),
          ingles,
        })
      );
    });

    return comoListaHtml(lineas);
  };

  const fechasEs = armarLineas(false);
  const fechasEn = armarLineas(true);

  await runTransaction(firebaseFirestore, async (transaction) => {
    // Firestore obliga a leer todo antes de escribir nada.
    const snaps = await Promise.all(
      salasImplicadas.map((id) => transaction.get(doc(firebaseFirestore, 'locations', id)))
    );

    /** Ocupación de cada sala, en memoria, que se va llenando fecha a fecha. */
    const agenda: Record<string, Record<string, Record<string, string[]>>> = {};
    salasImplicadas.forEach((id, i) => {
      agenda[id] = (snaps[i].data()?.reservations ?? {}) as Record<
        string,
        Record<string, string[]>
      >;
    });

    const tocados: Record<string, Set<string>> = {};

    for (const nueva of nuevas) {
      const sala = salas[nueva.locationId];
      const limite = Number(sala?.schedule?.settings?.numberBookingsAllow ?? 1);
      const plazas = plazasPorFranja(sala, personas);
      const dia = { ...(agenda[nueva.locationId][nueva.fecha] ?? {}) };

      // Se comprueba contra el estado real, que ya incluye las fechas que esta
      // misma serie acaba de ocupar: dos días iguales de la serie no pueden
      // colarse el uno al otro.
      for (const hora of nueva.franjas) {
        const ocupadas = (dia[hora] ?? []).length;
        if (ocupadas + plazas > limite) {
          throw new FechaOcupadaError(nueva.fecha, hora, sala?.name);
        }
      }

      for (const hora of nueva.franjas) {
        dia[hora] = [
          ...(dia[hora] ?? []),
          ...Array.from({ length: plazas }, () => nueva.ref.id),
        ];
      }

      agenda[nueva.locationId][nueva.fecha] = dia;
      (tocados[nueva.locationId] ??= new Set()).add(nueva.fecha);
    }

    // ── A partir de aquí, solo escrituras ──
    for (const nueva of nuevas) {
      const sala = salas[nueva.locationId];
      const inicio = getDatetimeFromString(nueva.fecha, horaInicio)!;
      const marcaInicio = getTimestampZonedTimeToUtc(inicio);
      const fin = horaFin ? getDatetimeFromString(nueva.fecha, horaFin) : null;
      const marcaFin = fin ? getTimestampZonedTimeToUtc(fin) : null;

      const copia: Record<string, any> = {
        ...base,
        date: nueva.fecha,
        datetime: marcaInicio,
        startDatetime: marcaInicio,
        startDate: formatDate(inicio),
        startHour: horaInicio,
        endDatetime: marcaFin,
        endDate: marcaFin ? formatDate(marcaFin.toDate()) : '',
        endHour: base.endHour ?? '--',
        time: base.time ?? horaInicio,
        location: sala ? salaParaGuardar(sala) : base.location,
        status: 'pending',
        isUpdatedByUser: false,
        createdAt: Date.now(),
        updatedAt: Date.now(),
        createdBy: creadaPor,
        // El rastro de la serie: de dónde salió y qué puesto ocupa en ella.
        recurrence: {
          id: recurrenceId,
          desde: baseId,
          indice: nueva.indice,
          total,
          descripcion,
        },
      };

      // Datos de la serie en campos planos: la plantilla del correo sustituye
      // `{{clave}}` por campos de primer nivel, y un objeto saldría como
      // "[object Object]".
      copia.recurrenceTotal = total;
      copia.recurrenceResumen = descripcion;
      copia.recurrenceFechas = fechasEs;
      copia.recurrenceFechasEn = fechasEn;

      // Solo la primera se queda sin código, para que sea la única que despierte
      // al servidor de correo. Las demás nacen con el suyo y no avisan a nadie.
      //
      // El `delete` es imprescindible: `copia` arrastra el código de la reserva
      // original por el spread de arriba. Sin borrarlo, la copia nacía con el
      // código de su madre y el cambio posterior NO era "de sin código a con
      // código" sino un código distinto, así que el servidor lo tomaba por una
      // modificación y mandaba el correo equivocado. Además, durante ese rato
      // había dos reservas con el mismo código.
      if (!avisarPorCorreo || conBase || nueva.indice !== 1) copia.code = nueva.code;
      else delete copia.code;

      // Campos que no deben heredarse de la original.
      delete copia.id;
      // Marca interna del panel para llevar el código apartado; no es un dato
      // de la reserva y no debe quedarse guardada.
      delete copia.__codigoPendiente;
      delete copia.statusHistory;
      delete copia.cancelledAt;
      delete copia.cancelledBy;
      delete copia.payment;

      transaction.set(nueva.ref, copia);
    }

    for (const [locationId, fechasTocadas] of Object.entries(tocados)) {
      const ref = doc(firebaseFirestore, 'locations', locationId);
      for (const fecha of fechasTocadas) {
        transaction.update(ref, { [`reservations.${fecha}`]: agenda[locationId][fecha] });
      }
    }
  });

  // El único cambio que despierta al servidor de correo.
  //
  // Cuando la reserva del día venía sin código, se le escribe aquí junto con
  // los datos de la serie: para el servidor eso es "acaba de recibir código",
  // así que manda un solo correo y ya con todas las fechas contadas. Si no,
  // avisa la primera copia, como cuando se repite una reserva que ya existía.
  const { updateReservation } = await import(
    '~/pages/public/Forms/Reservation/repositories/reservationRepository'
  );

  if (conBase) {
    await updateReservation(baseId, {
      code: codigoBasePendiente,
      recurrence: { id: recurrenceId, desde: baseId, indice: 1, total, descripcion },
      recurrenceTotal: total,
      recurrenceResumen: descripcion,
      recurrenceFechas: fechasEs,
      recurrenceFechasEn: fechasEn,
      updatedAt: Date.now(),
    });
  } else if (avisarPorCorreo) {
    await updateReservation(nuevas[0].ref.id, { code: nuevas[0].code });
  }

  return {
    creadas: nuevas.map((n) => ({
      code: n.code,
      fecha: n.fecha,
      locationName: salas[n.locationId]?.name ?? '',
    })),
  };
};
